(() => {
  if (window.PhotoRecognition) return;
  const buttonFor = (record, gallery) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'photo-recognition__evidence';
    button.dataset.recognitionOpen = record.id;
    button.textContent = record.label;
    button.setAttribute('aria-label', record.title + ' — ' + record.label);
    button.setAttribute('aria-haspopup', 'dialog');
    return button;
  };
  const append = (container, records, gallery, about = false) => {
    if (!records?.length) return;
    const list = document.createElement(about ? 'ul' : 'span');
    list.className = 'photo-recognition__list';
    for (const record of records) {
      const item = document.createElement(about ? 'li' : 'span');
      item.className = 'photo-recognition__item';
      if (about) {
        const title = document.createElement('a');
        title.className = 'photo-recognition__photograph';
        title.href = gallery.printUrl(record.handle);
        title.textContent = record.title;
        item.append(title);
        item.append(document.createTextNode(record.sentence?.startsWith(record.title) ? ' ' : ' — '));
      }
      if (record.evidence?.url) item.append(buttonFor(record, gallery));
      else {
        const text = document.createElement('span');
        text.textContent = about && record.sentence?.startsWith(record.title) ? record.sentence.slice(record.title.length).trimStart() : record.sentence || record.label;
        item.append(text);
      }
      item.append(document.createTextNode('.'));
      list.append(item);
    }
    container.append(list);
  };
  const parameter = 'recognition';
  const entries = new Map();
  let dialog, active, closing = false, restoreVersion = 0;
  const pageFor = (gallery, handle) => [...gallery.querySelectorAll('.photo-print')].find(page => page.dataset.photo === handle);
  const cleanUrl = value => {
    const url = new URL(value, location.origin);
    url.searchParams.delete(parameter);
    return url;
  };
  const triggersFor = (gallery, id) => [...gallery.querySelectorAll('[data-recognition-open]')].filter(button => button.dataset.recognitionOpen === id);
  const controlSelector = 'input[type="radio"], [data-print-frame], [data-print-mat], [data-lab-paper-choice], [data-artist-paper-choice], [data-sf-hardware]';
  const capture = (record, trigger, gallery) => {
    const page = trigger.closest('.photo-print');
    const wrapper = gallery.closest('.page-wrapper');
    return {
      version: 1, id: record.id, token: 'recognition-' + Date.now() + '-' + Math.random().toString(36).slice(2),
      origin: cleanUrl(location.href).href, managed: true,
      context: trigger.closest('.photo-gallery__bio') ? 'about' : page ? 'print' : 'photo',
      photo: page?.dataset.photo || record.handle,
      triggerIndex: Math.max(0, triggersFor(gallery, record.id).indexOf(trigger)),
      controls: page ? [...page.querySelectorAll(controlSelector)].map(control => ({value:control.value, checked:control.checked, type:control.type})) : [],
      details: page ? [...page.querySelectorAll('details')].map(detail => detail.open) : [],
      scroll: {x:window.scrollX, y:window.scrollY, wrapperTop:wrapper?.scrollTop || 0, wrapperLeft:wrapper?.scrollLeft || 0, dialogTop:gallery.dialog?.scrollTop || 0},
      scrollRestoration: history.scrollRestoration
    };
  };
  const restoreControls = entry => {
    const page = pageFor(entry.gallery, entry.data.photo);
    if (!page || entry.data.context !== 'print') return;
    const controls = [...page.querySelectorAll(controlSelector)];
    for (const [index, saved] of entry.data.controls.entries()) {
      const control = controls[index];
      if (!control || control.type !== saved.type) continue;
      if (control.type === 'radio') {
        if (control.value !== saved.value) continue;
        control.checked = !!saved.checked;
        if (control.checked) control.dispatchEvent(new Event('change', {bubbles:true}));
      } else if ([...control.options].some(option => option.value === saved.value)) {
        control.value = saved.value;
        control.dispatchEvent(new Event('change', {bubbles:true}));
      }
    }
    [...page.querySelectorAll('details')].forEach((detail,index) => { detail.open = !!entry.data.details[index]; });
  };
  const restoreOrigin = entry => {
    const version = ++restoreVersion;
    const restore = () => {
      if (active || version !== restoreVersion || cleanUrl(location.href).href !== entry.data.origin) return;
      const trigger = entry.trigger?.isConnected ? entry.trigger : triggersFor(entry.gallery, entry.data.id)[entry.data.triggerIndex];
      trigger?.focus({preventScroll:true});
      const wrapper = entry.gallery.closest('.page-wrapper');
      wrapper?.scrollTo({top:entry.data.scroll.wrapperTop,left:entry.data.scroll.wrapperLeft,behavior:'instant'});
      if (entry.gallery.dialog) entry.gallery.dialog.scrollTop = entry.data.scroll.dialogTop;
      window.scrollTo({top:entry.data.scroll.y,left:entry.data.scroll.x,behavior:'instant'});
    };
    restore();
    requestAnimationFrame(() => requestAnimationFrame(() => {
      restore();
      if (!active && version === restoreVersion) history.scrollRestoration = entry.data.scrollRestoration;
    }));
  };
  const hide = (restore = true) => {
    const previous = active;
    active = null;
    closing = false;
    if (dialog?.open) dialog.close();
    dialog?.replaceChildren();
    if (restore && previous) restoreOrigin(previous);
    return previous;
  };
  const requestClose = () => {
    if (!active || closing) return;
    closing = true;
    const current = history.state?.photoRecognition;
    if (current?.token === active.data.token && current.managed && new URL(location.href).searchParams.get(parameter) === current.id) {
      history.back();
    } else {
      const state = {...history.state};
      delete state.photoRecognition;
      history.replaceState(state, '', active.data.origin);
      hide();
    }
  };
  const ensureDialog = () => {
    if (dialog) return;
    dialog = document.createElement('dialog');
    dialog.className = 'photo-recognition__dialog';
    dialog.setAttribute('aria-labelledby', 'PhotoRecognitionTitle');
    dialog.addEventListener('cancel', event => { event.preventDefault(); requestClose(); });
    dialog.addEventListener('close', () => { if (active && !dialog.open) requestClose(); });
    dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = [...dialog.querySelectorAll('button')];
      event.preventDefault();
      const index = controls.indexOf(document.activeElement);
      controls[(index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length].focus();
    });
    document.body.append(dialog);
  };
  const show = entry => {
    if (active) return;
    ensureDialog();
    active = entry;
    closing = false;
    restoreVersion++;
    history.scrollRestoration = 'manual';
    const {record, gallery, data} = entry;
    const body = document.createElement('div');
    body.className = 'photo-recognition__body';
    const toolbar = document.createElement('div');
    toolbar.className = 'photo-recognition__toolbar';
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'photo-recognition__return';
    back.autofocus = true;
    back.textContent = data.context === 'about' ? gallery.dataset.recognitionReturnAbout : data.context === 'print' ? gallery.dataset.recognitionReturnPrint : gallery.dataset.recognitionReturnPhoto;
    back.addEventListener('click', requestClose);
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'photo-recognition__close';
    close.textContent = '×';
    close.setAttribute('aria-label', gallery.dataset.recognitionClose + ': ' + back.textContent);
    close.addEventListener('click', requestClose);
    toolbar.append(back, close);
    const title = document.createElement('h2');
    title.id = 'PhotoRecognitionTitle';
    title.textContent = record.title + ' — ' + record.label;
    body.append(toolbar, title);
    if (record.oldTitle) {
      const note = document.createElement('p');
      note.textContent = gallery.dataset.recognitionOldTitle.replace('[title]', record.oldTitle);
      body.append(note);
    }
    const image = document.createElement('img');
    Object.assign(image, {src:record.evidence.url,width:record.evidence.width,height:record.evidence.height,alt:record.evidence.alt,decoding:'async'});
    body.append(image);
    dialog.replaceChildren(body);
    dialog.showModal();
    back.focus({preventScroll:true});
  };
  const open = (record, trigger, gallery) => {
    if (active || closing || !record.evidence?.url) return;
    const data = capture(record, trigger, gallery);
    const entry = {record,trigger,gallery,data};
    entries.set(data.token, entry);
    const url = new URL(location.href);
    url.searchParams.set(parameter, record.id);
    history.pushState({...history.state,photoRecognition:data}, '', url.href);
    show(entry);
  };
  const ready = gallery => {
    if (active || !gallery?.review) return;
    const url = new URL(location.href);
    const id = url.searchParams.get(parameter);
    if (!id) return;
    const record = gallery.review.recognitionRecords?.find(item => item.id === id && item.evidence?.url);
    if (!record) {
      const state = {...history.state};
      delete state.photoRecognition;
      history.replaceState(state,'',cleanUrl(url.href).href);
      return;
    }
    let data = history.state?.photoRecognition;
    const valid = data?.version === 1 && data.id === id && data.managed && typeof data.token === 'string' && typeof data.origin === 'string' && cleanUrl(url.href).href === data.origin && Array.isArray(data.controls) && Array.isArray(data.details) && data.scroll;
    if (!valid) {
      const origin = cleanUrl(url.href);
      if (!origin.searchParams.has('review_page') && !origin.searchParams.has('review_print') && !origin.hash.startsWith('#photo=')) {
        const fallback = new URL(gallery.printUrl(record.handle),location.origin);
        if (fallback.pathname !== origin.pathname) {
          fallback.searchParams.set(parameter,id);
          location.replace(fallback.href);
          return;
        }
        origin.searchParams.set('review_print',record.handle);
      }
      const state = {...history.state};
      delete state.photoRecognition;
      history.replaceState(state,'',origin.href);
      if (origin.searchParams.has('review_print')) gallery.showRequestedPrint();
      const trigger = triggersFor(gallery,id)[0];
      if (!trigger) return;
      data = capture(record,trigger,gallery);
      const evidenceUrl = new URL(origin.href);
      evidenceUrl.searchParams.set(parameter,id);
      history.pushState({...state,photoRecognition:data},'',evidenceUrl.href);
    }
    let entry = entries.get(data.token);
    if (!entry) {
      const triggers = triggersFor(gallery,id);
      entry = {record,gallery,data,trigger:triggers[data.triggerIndex] || triggers[0]};
      entries.set(data.token,entry);
    }
    restoreControls(entry);
    show(entry);
  };
  window.addEventListener('popstate', event => {
    const id = new URL(location.href).searchParams.get(parameter);
    if (!active && !id && !history.state?.photoRecognition) return;
    const previous = active ? hide(false) : null;
    if (id) {
      event.stopImmediatePropagation();
      const gallery = previous?.gallery || document.querySelector('photo-gallery[data-preview]');
      ready(gallery);
    } else if (previous && cleanUrl(location.href).href === previous.data.origin) {
      event.stopImmediatePropagation();
      restoreOrigin(previous);
    }
  }, true);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-recognition-open]');
    if (!trigger) return;
    const gallery = trigger.closest('photo-gallery');
    const record = gallery?.review?.recognitionRecords?.find(item => item.id === trigger.dataset.recognitionOpen);
    if (!record) return;
    event.preventDefault();
    open(record, trigger, gallery);
  });
  window.PhotoRecognition = {append,ready};
  const restoreReady = () => document.querySelectorAll('photo-gallery[data-preview]').forEach(gallery => {
    if (!gallery.review) return;
    gallery.querySelectorAll('.photo-print').forEach(page => {
      const photo = gallery.review.photos.find(item => item.handle === page.dataset.photo);
      page.querySelectorAll('.photo-print__description').forEach(description => {
        if (!description.querySelector('.photo-recognition__list')) append(description, photo?.recognition, gallery);
      });
    });
    const copy = gallery.querySelector('.photo-gallery__bio-copy');
    const about = gallery.review.pages?.find(page => page.handle === 'about-the-photographer');
    if (copy && about && !copy.querySelector('.photo-recognition__list')) {
      const marker = copy.children[about.recognitionParagraphIndex ?? 3];
      append(copy, gallery.review.recognitionRecords, gallery, true);
      if (marker) marker.after(copy.querySelector('.photo-recognition__list'));
    }
    ready(gallery);
  });
  document.addEventListener('DOMContentLoaded', restoreReady, {once:true});
  if (document.readyState === 'complete') restoreReady();
})();
